/**
 * MatchRules (GDD sections 2 and 3): placement, then turns in lobby order. Each turn: selection and aim
 * (30 s), retreat after the shot (3 s), resolution until everything is calm (10 s at most), contact
 * damage, next player. Commands are filtered and bookkept here; the physics stay in tick().
 * Everything is plain integers in GameState, so it is hashed and replayed like the rest.
 */
import { STATE_LIQUID, FIRE, LAVA, WATER, OBSIDIAN, ACID, isqrt, nextInt, restLiquids } from '@invaders/sim';
import { isSandboxCommand, type Command } from './commands';
import { FP_SHIFT, FROZEN_NOW, FROZEN_PENDING, PFP, PFP_SHIFT, TICKS_PER_SECOND, type GameState, type Unit } from './state';
import { INVADERS, LAVAMAN, isInvaderKind } from './invaders';
import { UNIT_H, UNIT_W, boxFree, createUnit, damageUnit, healUnit, isSettled, killUnit, onLava, touches, unitCenter } from './unit';
import { WEAPONS, isWeapon } from './weapons';
import { PROJ_LAVA_BOMB, WEAPON_DRILL, WEAPON_JETPACK, WEAPON_PISTOL } from './weapon-ids';
import { triggerLegacies } from './legacy';
import { dropChest, ghostChest } from './chests';
import { batteriesTurnStart } from './battery';
import { barrelMoving, barrelsTurnStart } from './barrel';
import { roombasTurnStart } from './roomba';
import { volcanoesTurnStart } from './volcano';
import { MINE_EXPLOSIVE, MINE_HALF, placeMine } from './mines';
import { mapTurnStart } from './maps/events';

export const PHASE_PLACEMENT = 0;
export const PHASE_AIM = 1;
export const PHASE_RETREAT = 2;
export const PHASE_RESOLVE = 3;
export const PHASE_OVER = 4;
/**
 * A ghost turn (author's addition): an eliminated player keeps a short turn in the rotation, to drop a chest over the
 * column it chooses or to put an explosive mine where it wants (one of the two), then the next player.
 */
export const PHASE_GHOST = 5;
export const GHOST_TICKS = 15 * TICKS_PER_SECOND;

export const PLACEMENT_TICKS = 20 * TICKS_PER_SECOND;
export const AIM_TICKS = 30 * TICKS_PER_SECOND;
export const RETREAT_TICKS = 3 * TICKS_PER_SECOND;
export const RESOLVE_MAX_TICKS = 10 * TICKS_PER_SECOND;
/** Shortest resolution, so that the end of a turn stays readable. Not in the GDD: tuning value. */
export const RESOLVE_MIN_TICKS = TICKS_PER_SECOND / 2;
/** GDD: placement is refused closer than this to any enemy (distance between centres, in cells). */
export const PLACEMENT_ENEMY_DISTANCE = 48;
export const MAX_WIND = 10;
/** GDD section 6: contact damage of the turn. */
export const LAVA_DAMAGE = 25;
export const WATER_HURT = 5;
export const WATER_HEAL = 5;
/** GDD: fire on contact 10 HP, then a burn of 5 HP at the next turn. */
export const FIRE_DAMAGE = 10;
export const BURN_DAMAGE = 5;
/** GDD: acid on contact. */
export const ACID_DAMAGE = 10;
/**
 * GDD section 3: the rising lava climbs 4 cells at the first round (12 by the author's choice), then more each round.
 * It starts from the lowest cell it can fill (the deepest sea floor, a gallery), not from the bottom of the map: the
 * rock under it would hide its first rises (author's rule).
 */
export const LAVA_FIRST_RISE = 12;
/**
 * Each round it rises this much more than the last (author's choice: 12, 16, 20…, against the GDD's 4, 5, 6…, which
 * stayed in the rock under the map for the whole match): from the deepest sea floor up to the sea level in 5 rounds.
 */
export const LAVA_RISE_STEP = 4;
/** Rounds of rising lava after which the player with the most HP wins. */
export const SAFETY_NET_ROUNDS = 30;
/**
 * The end of the world (author's addition): once the lava rises, lava bombs (those of the eruption, Île de Braise)
 * fall from the sky at the start of every turn, at random places of the whole map: STONE_RAIN_FIRST at the first
 * round of the rising lava, one more each round, STONE_RAIN_MAX at most (the eruption's 25).
 */
export const STONE_RAIN_FIRST = 5;
export const STONE_RAIN_MAX = 25;
/** They start up to this many cells above the map, so that they rain down rather than land all at once. */
const STONE_RAIN_HEIGHT = 240;

export const NO_WINNER = -1;
export const DRAW = -2;

/**
 * Players in a match: 2 to 8 (author's choice; 4 in the GDD), 1 to 4 invaders each, and at most 16 invaders on the map
 * (players × invaders: 8 players have 2 each, 5 have 3).
 */
export const MAX_PLAYERS = 8;
export const MAX_UNITS_PER_PLAYER = 4;
export const MAX_UNITS_ON_MAP = 16;

/** The most invaders each of `players` may have. */
export function maxUnitsPerPlayer(players: number): number {
  return Math.min(MAX_UNITS_PER_PLAYER, Math.floor(MAX_UNITS_ON_MAP / Math.max(1, players)));
}

export interface MatchConfig {
  /** 2 to MAX_PLAYERS. */
  readonly players: number;
  /** 1 to maxUnitsPerPlayer(players). */
  readonly unitsPerPlayer: number;
  /** Rounds before the lava rises, 3 to 40 (GDD section 2: 10 to 40). */
  readonly roundsBeforeLava: number;
  /**
   * The host's choice of weapons (author's addition): the ammunition each team starts with, by weapon id (-1 =
   * unlimited, 0 = none, at most MAX_START_AMMO). Left out: the GDD's arsenal (WEAPONS[i].ammo).
   */
  readonly ammo?: readonly number[];
  /** The chest weapons a chest may hold (the medkit always may). Left out: all of them. */
  readonly chestWeapons?: readonly number[];
  /** Seconds of the aim of a turn (author's option; the GDD's 30), MIN_TURN_SECONDS to MAX_TURN_SECONDS. */
  readonly turnSeconds?: number;
  /** False: a team's weapons hurt none of its own units (author's option; left out: they do, as in the GDD). */
  readonly friendlyFire?: boolean;
  /**
   * True: during the placement a player does not see the others' invaders, and may put one close to an enemy (author's
   * option: no 48 cells, nor a refusal that would give an enemy away). Only with the free placement.
   */
  readonly hiddenPlacement?: boolean;
  /**
   * True: no free placement (author's option, unchecked « Placement libre »): the computer draws every invader and its
   * spot at the start, and the first turn begins at once. Left out: the players place their invaders (GDD section 2).
   */
  readonly autoPlacement?: boolean;
  /**
   * True: an eliminated player keeps its ghost turns (author's option, « Tour des fantômes »). Left out: it plays no more,
   * and the turns go round the players still in the match.
   */
  readonly ghostTurns?: boolean;
}

/** The host's choice of the time of a turn's aim (the GDD's 30 s by default). */
export const MIN_TURN_SECONDS = 10;
export const MAX_TURN_SECONDS = 120;

/** Ticks of the aim of a turn in this match. */
export function aimTicks(m: MatchState): number {
  return (m.config.turnSeconds ?? 30) * TICKS_PER_SECOND;
}

/** The pistol fires this many bullets in a turn (author's choice), the turn going on until the last one. */
export const PISTOL_SHOTS = 3;

/** The most ammunition of a weapon the host may give each team at the start (or unlimited). */
export const MAX_START_AMMO = 9;

/** Fewest rounds before the lava: 10 in the GDD, 3 by the author's choice (the most is 40). */
export const MIN_ROUNDS_BEFORE_LAVA = 3;

export const DEFAULT_MATCH: MatchConfig = { players: 2, unitsPerPlayer: 3, roundsBeforeLava: 20 };

/** What a lobby (or a replay log) gives: the lava delay may be left out (default 20), and the arsenal (the GDD's). */
export type MatchSetup = Omit<MatchConfig, 'roundsBeforeLava'> & { readonly roundsBeforeLava?: number };

export interface MatchState {
  readonly config: MatchConfig;
  phase: number;
  /** Ticks since the phase started. */
  phaseTick: number;
  currentPlayer: number;
  /** 1-based once the first turn starts. */
  turn: number;
  /** 1-based; a round is over when every player still in the game has played a turn. */
  round: number;
  /** Highlighted unit of the current player, -1 if none. */
  selected: number;
  locked: boolean;
  moved: boolean;
  fired: boolean;
  /** Per player: index, among its units in placement order, of the one highlighted first next turn. */
  rotation: number[];
  /** ammo[player * WEAPONS.length + weapon], -1 = unlimited. */
  ammo: number[];
  winner: number;
  /** Height of the rising lava above the bottom of the map, in cells (0 until it starts). */
  lavaLevel: number;
  /** Pistol bullets fired this turn (PISTOL_SHOTS at most: the shot of the turn is over after the last one). */
  pistolShots: number;
  /** Players the host excluded (online: bit `player`): their invaders are gone, they play no more turns. */
  out: number;
  /** The unit of the turn touched the lava this turn and took LAVA_DAMAGE at once (once a turn, author's rule). */
  lavaBurnt: boolean;
}

export function createMatchState(config: MatchSetup = DEFAULT_MATCH): MatchState {
  const players = Math.min(MAX_PLAYERS, Math.max(2, Math.trunc(config.players) || 2));
  const unitsPerPlayer = Math.min(maxUnitsPerPlayer(players), Math.max(1, Math.trunc(config.unitsPerPlayer) || 1));
  const roundsBeforeLava = Math.min(40, Math.max(MIN_ROUNDS_BEFORE_LAVA, Math.trunc(config.roundsBeforeLava ?? DEFAULT_MATCH.roundsBeforeLava) || DEFAULT_MATCH.roundsBeforeLava));
  const start = config.ammo ? WEAPONS.map((w, i) => startAmmo(config.ammo![i], w.ammo, w.chest)) : undefined;
  const chestWeapons = config.chestWeapons
    ? WEAPONS.map((_, i) => i).filter((i) => WEAPONS[i].chest && config.chestWeapons!.includes(i))
    : undefined;
  const ammo: number[] = [];
  for (let p = 0; p < players; p++) WEAPONS.forEach((w, i) => ammo.push(start ? start[i] : w.ammo));
  const turnSeconds = config.turnSeconds === undefined ? undefined
    : Math.min(MAX_TURN_SECONDS, Math.max(MIN_TURN_SECONDS, Math.trunc(config.turnSeconds) || 30));
  return {
    config: {
      players, unitsPerPlayer, roundsBeforeLava, ...(start ? { ammo: start } : {}), ...(chestWeapons ? { chestWeapons } : {}),
      ...(turnSeconds !== undefined ? { turnSeconds } : {}),
      ...(config.friendlyFire === false ? { friendlyFire: false } : {}),
      ...(config.hiddenPlacement === true && config.autoPlacement !== true ? { hiddenPlacement: true } : {}),
      ...(config.autoPlacement === true ? { autoPlacement: true } : {}),
      ...(config.ghostTurns === true ? { ghostTurns: true } : {}),
    },
    phase: PHASE_PLACEMENT,
    phaseTick: 0,
    currentPlayer: 0,
    turn: 0,
    round: 0,
    selected: -1,
    locked: false,
    moved: false,
    fired: false,
    rotation: new Array<number>(players).fill(0),
    ammo,
    winner: NO_WINNER,
    lavaLevel: 0,
    pistolShots: 0,
    out: 0,
    lavaBurnt: false,
  };
}

/**
 * A starting ammunition from a lobby: -1 (unlimited) or 0 to MAX_START_AMMO; anything else, the GDD's. A chest weapon
 * is never unlimited (author's rule): -1 gives it MAX_START_AMMO.
 */
function startAmmo(v: number | undefined, gdd: number, chest = false): number {
  if (!Number.isInteger(v) || v! < -1 || v! > MAX_START_AMMO) return gdd;
  return v === -1 && chest ? MAX_START_AMMO : v!;
}

/**
 * The arsenal as a short text for the lobby (the server relays it without reading it): one character per weapon for
 * its starting ammunition ('i' = unlimited, '0' to '9'), a dot, then one per weapon, '1' if a chest may hold it.
 * '' = the GDD's arsenal.
 */
export function encodeArsenal(ammo: readonly number[], chestWeapons: readonly number[]): string {
  const a = WEAPONS.map((_, i) => (ammo[i] < 0 ? 'i' : String(Math.min(MAX_START_AMMO, Math.max(0, ammo[i] | 0))))).join('');
  const c = WEAPONS.map((_, i) => (chestWeapons.includes(i) ? '1' : '0')).join('');
  return `${a}.${c}`;
}

/** Back from encodeArsenal: the config's `ammo` and `chestWeapons`, nothing for '' or a text it cannot read. */
export function decodeArsenal(text: string): { ammo?: number[]; chestWeapons?: number[] } {
  const m = /^([0-9i]+)\.([01]+)$/.exec(text);
  if (!m) return {};
  const ammo = WEAPONS.map((w, i) => (i < m[1].length ? (m[1][i] === 'i' ? (w.chest ? MAX_START_AMMO : -1) : Number(m[1][i])) : w.ammo));
  const chestWeapons = WEAPONS.map((_, i) => i).filter((i) => WEAPONS[i].chest && (i < m[2].length ? m[2][i] === '1' : true));
  return { ammo, chestWeapons };
}

/** The GDD's arsenal in the form of the host's choice. */
export function defaultArsenal(): { ammo: number[]; chestWeapons: number[] } {
  return { ammo: WEAPONS.map((w) => w.ammo), chestWeapons: WEAPONS.map((_, i) => i).filter((i) => WEAPONS[i].chest) };
}

/** Rise of the lava at the start of `round` (0 before the rounds of the lobby are over). */
export function lavaRiseAt(m: MatchState, round: number): number {
  const n = round - m.config.roundsBeforeLava;
  return n >= 1 ? LAVA_FIRST_RISE + (n - 1) * LAVA_RISE_STEP : 0;
}

/** Lava bombs falling from the sky at the start of every turn of `round` (0 before the end of the world). */
export function stoneRainAt(m: MatchState, round: number): number {
  const n = round - m.config.roundsBeforeLava;
  return n >= 1 ? Math.min(STONE_RAIN_MAX, STONE_RAIN_FIRST + n - 1) : 0;
}

/** True once the lava has started to rise. */
export function lavaRising(m: MatchState): boolean {
  return m.lavaLevel > 0;
}

export function playerUnits(state: GameState, player: number): Unit[] {
  return state.units.filter((u) => u.player === player);
}

export function ammoLeft(m: MatchState, player: number, weapon: number): number {
  return m.ammo[player * WEAPONS.length + weapon];
}

// --- placement ------------------------------------------------------------------------------------

/** `near`: refused only for being too close to an enemy. */
export type PlacementResult = { ok: true; x: number; y: number } | { ok: false; reason: string; x?: number; y?: number; near?: boolean };

/**
 * Where an invader of `player` put with its centre at cell (x, y) would stand (top-left cell of its box),
 * or why it cannot: GDD section 2 — firm ground (no water, lava, fire or oil), room for 16 × 16, more than
 * `distance` cells from any enemy (48, unless placementDistance relaxes it).
 */
export function placementSpot(state: GameState, player: number, x: number, y: number, distance = PLACEMENT_ENEMY_DISTANCE): PlacementResult {
  const world = state.world;
  const { w, h, data } = world.physique;
  const matState = world.rules.materials.state;
  if (!Number.isInteger(x) || !Number.isInteger(y)) return { ok: false, reason: 'invalid position' };
  const cx = x - (UNIT_W >> 1);
  let cy = Math.max(0, y - (UNIT_H >> 1));
  if (cx < 0 || cx + UNIT_W > w || cy + UNIT_H > h) return { ok: false, reason: 'off the map' };
  if (!boxFree(world, cx, cy)) return { ok: false, reason: 'no room' };
  // It falls while the row under its box is free (the box itself is: one row more at each step).
  while (cy + UNIT_H < h && rowFree(world, cx, cy + UNIT_H)) cy++;
  if (cy + UNIT_H >= h) return { ok: false, reason: 'no ground', x: cx, y: cy };
  for (let yy = cy; yy <= cy + UNIT_H; yy++) {
    for (let xx = cx; xx < cx + UNIT_W; xx++) {
      const m = data[yy * w + xx];
      if (matState[m] === STATE_LIQUID || m === FIRE) return { ok: false, reason: 'unstable ground', x: cx, y: cy };
    }
  }
  const mx = cx + (UNIT_W >> 1);
  const my = cy + (UNIT_H >> 1);
  // Hidden placement: the enemies are not seen, so they neither stand in the way nor keep anyone at a distance.
  const hidden = state.match?.config.hiddenPlacement === true && state.match.phase === PHASE_PLACEMENT;
  for (const u of state.units) {
    if (!u.alive || (hidden && u.player !== player)) continue;
    const ux = u.x >> FP_SHIFT;
    const uy = u.y >> FP_SHIFT;
    if (cx < ux + UNIT_W && ux < cx + UNIT_W && cy < uy + UNIT_H && uy < cy + UNIT_H) return { ok: false, reason: 'occupied', x: cx, y: cy };
    if (u.player === player) continue;
    const [ex, ey] = unitCenter(u);
    const d = isqrt((ex - mx) * (ex - mx) + (ey - my) * (ey - my));
    if (d <= distance) return { ok: false, reason: 'too close to an enemy', x: cx, y: cy, near: true };
  }
  return { ok: true, x: cx, y: cy };
}

/** The UNIT_W cells of row y from column cx let a unit through (empty, gas or liquid). */
function rowFree(world: GameState['world'], cx: number, y: number): boolean {
  const { w, data } = world.physique;
  const matState = world.rules.materials.state;
  for (let x = cx; x < cx + UNIT_W; x++) if (matState[data[y * w + x]] < STATE_LIQUID) return false;
  return true;
}

/** Distances to the enemies tried in turn when the GDD's leaves no valid spot. */
const RELAXED_DISTANCES = [PLACEMENT_ENEMY_DISTANCE, 36, 24, 12, 0];

/**
 * How far from every enemy `player` must put an invader: the GDD's 48 cells, or less when no spot of the map keeps
 * 48 (author's addition: up to 8 teams and 16 invaders do not always fit an island at 48 from every enemy). Scans the
 * map: a few milliseconds.
 */
export function placementDistance(state: GameState, player: number): number {
  const w = state.world.physique.w;
  for (const d of RELAXED_DISTANCES) {
    for (let x = UNIT_W >> 1; x < w - (UNIT_W >> 1); x += 4) if (placementSpot(state, player, x, UNIT_H >> 1, d).ok) return d;
  }
  return 0;
}

/**
 * Kinds the player has not placed yet. A team may have the same invader several times (author's rule: the GDD allows no
 * duplicate), but the random draws (20 s without a choice, the computer's placement) take one of these, for variety.
 */
export function unusedKinds(state: GameState, player: number): number[] {
  const used = new Set(playerUnits(state, player).map((u) => u.kind));
  return INVADERS.map((_, k) => k).filter((k) => !used.has(k));
}

/** `distance` to the enemies: placementDistance's if known, else the GDD's unless no spot keeps it. */
function place(state: GameState, player: number, kind: number, x: number, y: number, distance?: number): boolean {
  if (!isInvaderKind(kind)) return false;
  let spot = placementSpot(state, player, x, y, distance ?? PLACEMENT_ENEMY_DISTANCE);
  if (!spot.ok && spot.near && distance === undefined) spot = placementSpot(state, player, x, y, placementDistance(state, player));
  if (!spot.ok) return false;
  state.units.push(createUnit(state.units.length, player, kind, spot.x, spot.y));
  nextPlacement(state);
  return true;
}

/** Every player still in the match has put all its invaders. */
function placementDone(state: GameState): boolean {
  const m = state.match!;
  for (let p = 0; p < m.config.players; p++) if (!isOut(m, p) && playerUnits(state, p).length < m.config.unitsPerPlayer) return false;
  return true;
}

/** After a placement: the next player who has invaders left to put (in turn), or the first turn once all are down. */
function nextPlacement(state: GameState): void {
  const m = state.match!;
  if (placementDone(state)) { startTurn(state, nextPlayer(state, m.config.players - 1)); return; }
  for (let k = 1; k <= m.config.players; k++) {
    const p = (m.currentPlayer + k) % m.config.players;
    if (!isOut(m, p) && playerUnits(state, p).length < m.config.unitsPerPlayer) { m.currentPlayer = p; break; }
  }
  m.phaseTick = 0;
}

/** GDD: without a choice after 20 s, a random invader is put on a random valid spot. False if none was found. */
function autoPlace(state: GameState): boolean {
  const m = state.match!;
  const rng = state.world.rng;
  const kinds = unusedKinds(state, m.currentPlayer);
  const kind = kinds[nextInt(rng, kinds.length)];
  const w = state.world.physique.w;
  const distance = placementDistance(state, m.currentPlayer);
  for (let attempt = 0; attempt < 256; attempt++) {
    const x = (UNIT_W >> 1) + nextInt(rng, w - UNIT_W);
    if (place(state, m.currentPlayer, kind, x, UNIT_H >> 1, distance)) return true;
  }
  // No random spot found: scan the columns in order.
  for (let x = UNIT_W >> 1; x < w - (UNIT_W >> 1); x += 4) if (place(state, m.currentPlayer, kind, x, UNIT_H >> 1, distance)) return true;
  return false;
}

// --- turns ------------------------------------------------------------------------------------------

function hasLivingUnits(state: GameState, player: number): boolean {
  return state.units.some((u) => u.alive && u.player === player);
}

/**
 * A unit the player may choose this turn: alive and not frozen. A unit frozen during its own team's turn cannot move
 * either (author's rule), and it still misses the next turn of its team.
 */
export function playable(u: Unit): boolean {
  return u.alive && u.frozen === 0;
}

/** The host excluded `player` (online). */
export function isOut(m: MatchState, player: number): boolean {
  return ((m.out >> player) & 1) !== 0;
}

/**
 * `player` plays turns: not excluded, and with living units — or eliminated, when the host allows the ghost turns.
 */
export function inRotation(state: GameState, player: number): boolean {
  const m = state.match!;
  return !isOut(m, player) && (m.config.ghostTurns === true || hasLivingUnits(state, player));
}

/** First playable unit of the player from its rotation index, or -1. */
function defaultSelection(state: GameState, player: number): number {
  const m = state.match!;
  const units = playerUnits(state, player);
  for (let i = 0; i < units.length; i++) {
    const u = units[(m.rotation[player] + i) % units.length];
    if (playable(u)) return u.id;
  }
  return -1;
}

/** Between turns: nobody walks, and the jetpacks are taken off (a unit in the air falls). */
function stopWalking(state: GameState): void {
  for (const u of state.units) { u.walkDir = 0; u.walkAcc = 0; u.jet = 0; u.thrust = false; u.drill = 0; }
}

/**
 * The lava rises from the bottom of the map: the empty, gas and liquid cells of the new rows become lava;
 * the terrain stays, so units may still stand on drowned rock (25 HP per turn). Lava next to water sets into
 * obsidian with steam (the reaction), so it freezes at the sea; the summit still ends under the lava.
 */
function raiseLava(state: GameState, rise: number): void {
  const m = state.match!;
  const world = state.world;
  const { w, h, data } = world.physique;
  const matState = world.rules.materials.state;
  if (m.lavaLevel === 0) m.lavaLevel = buriedRows(state);
  const from = Math.max(0, h - m.lavaLevel - rise);
  const to = h - m.lavaLevel - 1;
  for (let y = from; y <= to; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (matState[data[i]] >= STATE_LIQUID) { data[i] = LAVA; world.cellState.data[i] = 0; }
    }
  }
  m.lavaLevel += rise;
  restLiquids(world, 0, from, w - 1, to);
}

/**
 * Rows at the bottom of the map without a cell the lava could fill (only terrain): the lava starts above them, so that
 * its first rise is seen (author's rule; at the bottom of the map it stayed in the rock for rounds).
 */
function buriedRows(state: GameState): number {
  const { w, h, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  for (let y = h - 1; y >= 0; y--) {
    for (let x = 0; x < w; x++) if (matState[data[y * w + x]] >= STATE_LIQUID) return h - 1 - y;
  }
  return h;
}

/** The end of the world: the turn's lava bombs start falling from above the map, each at a random column, slanting a little. */
function stoneRain(state: GameState): void {
  const m = state.match!;
  const count = stoneRainAt(m, m.round);
  if (count === 0) return;
  const rng = state.world.rng;
  const w = state.world.physique.w;
  for (let k = 0; k < count; k++) {
    const x = nextInt(rng, w);
    const y = -8 - nextInt(rng, STONE_RAIN_HEIGHT);
    const vx = ((nextInt(rng, 9) - 4) * PFP) >> 3; // -0.5 to 0.5 cells per tick
    state.projectiles.push({
      x: (x << PFP_SHIFT) + (PFP >> 1), y: (y << PFP_SHIFT) + (PFP >> 1), vx, vy: 0, weapon: PROJ_LAVA_BOMB, owner: -1, age: 0, link: -1,
    });
  }
  state.events.push({ type: 'stoneRain', count });
}

/** Total HP of each player. */
function totalHp(state: GameState, player: number): number {
  return state.units.reduce((t, u) => (u.alive && u.player === player ? t + u.hp : t), 0);
}

/** GDD: 30 rounds after the lava started, the player with the most HP wins; equal HP is a draw. */
function safetyNet(state: GameState): void {
  const m = state.match!;
  let best = -1;
  let bestHp = -1;
  let tie = false;
  for (let p = 0; p < m.config.players; p++) {
    const hp = totalHp(state, p);
    if (hp === 0) continue;
    if (hp > bestHp) { best = p; bestHp = hp; tie = false; } else if (hp === bestHp) tie = true;
  }
  m.phase = PHASE_OVER;
  m.phaseTick = 0;
  m.winner = tie || best < 0 ? DRAW : best;
  stopWalking(state);
}

function startTurn(state: GameState, player: number): void {
  const m = state.match!;
  // GDD: a frozen unit misses the next turn of its team. The units that missed this one thaw; the newly
  // frozen ones of the player to play miss this turn.
  for (const u of state.units) {
    if (u.frozen === FROZEN_NOW) u.frozen = 0;
    else if (u.frozen === FROZEN_PENDING && u.player === player) u.frozen = FROZEN_NOW;
  }
  if (m.turn === 0 || player <= m.currentPlayer) {
    m.round++;
    if (m.round > m.config.roundsBeforeLava + SAFETY_NET_ROUNDS) { safetyNet(state); return; }
    const rise = lavaRiseAt(m, m.round);
    if (rise > 0) raiseLava(state, rise);
  }
  m.phase = PHASE_AIM;
  m.phaseTick = 0;
  m.turn++;
  m.currentPlayer = player;
  m.locked = false;
  m.moved = false;
  m.fired = false;
  m.pistolShots = 0;
  m.lavaBurnt = false;
  // GDD section 3, step 1: the wind changes, a chest is dropped, then the active legacies fire.
  state.wind = state.map.airless ? 0 : nextInt(state.world.rng, 2 * MAX_WIND + 1) - MAX_WIND;
  dropChest(state);
  triggerLegacies(state);
  batteriesTurnStart(state);
  barrelsTurnStart(state);
  roombasTurnStart(state);
  volcanoesTurnStart(state);
  mapTurnStart(state);
  stoneRain(state);
  // Chosen after the legacies and the batteries, which may have frozen or killed a unit.
  m.selected = defaultSelection(state, player);
  // Eliminated: a ghost turn. All its units frozen: the player misses the turn.
  if (!hasLivingUnits(state, player)) m.phase = PHASE_GHOST;
  else if (m.selected < 0) toResolve(state);
  stopWalking(state);
}

/**
 * The player after `after` who plays turns (inRotation): the eliminated ones are skipped, unless the host allows the
 * ghost turns; the excluded ones always are.
 */
function nextPlayer(state: GameState, after = state.match!.currentPlayer): number {
  const n = state.match!.config.players;
  for (let k = 1; k <= n; k++) {
    const p = (after + k) % n;
    if (inRotation(state, p)) return p;
  }
  return (after + 1) % n;
}

/**
 * Online: the host excluded `player` (the server sends this in its name). Its invaders vanish (no death burst, no
 * stele), it plays no more turns; its turn, if it was playing, goes to the resolution. Alone in the match, the other
 * player wins.
 */
function forfeit(state: GameState, player: number): void {
  const m = state.match!;
  if (!Number.isInteger(player) || player < 0 || player >= m.config.players || isOut(m, player)) return;
  m.out |= 1 << player;
  for (const u of state.units) if (u.player === player) killUnit(state, u, true);
  let left = 0;
  let last = -1;
  for (let p = 0; p < m.config.players; p++) if (!isOut(m, p)) { left++; last = p; }
  if (left <= 1) {
    m.phase = PHASE_OVER;
    m.phaseTick = 0;
    m.winner = left === 1 ? last : DRAW;
    stopWalking(state);
    return;
  }
  if (m.phase === PHASE_PLACEMENT) {
    if (m.currentPlayer === player || placementDone(state)) nextPlacement(state);
  } else if (m.currentPlayer === player && (m.phase === PHASE_AIM || m.phase === PHASE_RETREAT || m.phase === PHASE_GHOST)) toResolve(state);
}

/** A ghost turn's action: a chest over column x, or an explosive mine on the ground under (x, y). */
function ghostAction(state: GameState, player: number, action: 'chest' | 'mine', x: number, y: number): boolean {
  const { w, h, data } = state.world.physique;
  if (!Number.isInteger(x) || !Number.isInteger(y) || x < 0 || x >= w) return false;
  if (action === 'chest') return ghostChest(state, x) !== null;
  if (action !== 'mine') return false;
  const matState = state.world.rules.materials.state;
  let gy = Math.max(0, y);
  while (gy < h && matState[data[gy * w + x]] >= STATE_LIQUID) gy++;
  if (gy >= h || gy - MINE_HALF - 1 < 0) return false;
  const owner = playerUnits(state, player)[0];
  if (!owner) return false;
  placeMine(state, x, gy - MINE_HALF - 1, owner.id, MINE_EXPLOSIVE);
  return true;
}

function toResolve(state: GameState): void {
  const m = state.match!;
  m.phase = PHASE_RESOLVE;
  m.phaseTick = 0;
  stopWalking(state);
}

/**
 * GDD step 4: contact damage of the turn, applied once at the resolution, after the sensitivities:
 * fire 10 then a burn of 5 at the next turn, acid 10, lava 25 (the rising lava hurts everyone); water puts a burn
 * out, hurts Lavaman and Fireman (5), covers Lavaman with obsidian, and heals Treeman (5). Drowning is not here: the
 * head under water kills at once (unit.ts).
 */
function endOfTurnDamage(state: GameState): void {
  const m = state.match!;
  const world = state.world;
  const lavaTop = world.physique.h - m.lavaLevel;
  for (const u of state.units) {
    if (!u.alive) continue;
    const kind = INVADERS[u.kind];
    const inWater = touches(world, u, WATER);
    // The burn of the fire caught last turn (water puts it out), then the fire touching it now.
    if (u.burning) { u.burning = false; if (!inWater) damageUnit(state, u, BURN_DAMAGE, 'fire'); }
    if (touches(world, u, FIRE) && kind.fire > 0) {
      damageUnit(state, u, FIRE_DAMAGE, 'fire');
      u.burning = true;
    }
    if (touches(world, u, ACID)) damageUnit(state, u, ACID_DAMAGE, 'acid');
    if (touches(world, u, LAVA)) {
      const inRisingLava = m.lavaLevel > 0 && (u.y >> FP_SHIFT) + UNIT_H >= lavaTop - 1;
      damageUnit(state, u, LAVA_DAMAGE, inRisingLava ? 'risingLava' : 'lava');
    }
    if (inWater) {
      u.burning = false;
      if (kind.water === 'hurt') damageUnit(state, u, WATER_HURT, 'water');
      else if (kind.water === 'heal') healUnit(u, WATER_HEAL);
      if (u.kind === LAVAMAN && u.alive) coatInObsidian(state, u);
    }
  }
}

/**
 * The unit of the turn walks (or floats) on lava (author's rule): it takes LAVA_DAMAGE at once, once a turn, and again
 * at the resolution if it still touches lava.
 */
function lavaBurn(state: GameState): void {
  const m = state.match!;
  if (m.lavaBurnt || !m.locked || (m.phase !== PHASE_AIM && m.phase !== PHASE_RETREAT)) return;
  const u = state.units[m.selected];
  if (!u || !u.alive || !onLava(state.world, u)) return;
  m.lavaBurnt = true;
  const inRisingLava = m.lavaLevel > 0 && (u.y >> FP_SHIFT) + UNIT_H >= state.world.physique.h - m.lavaLevel - 1;
  damageUnit(state, u, LAVA_DAMAGE, inRisingLava ? 'risingLava' : 'lava');
}

/**
 * Author's rules: the turn ends at once when its unit dies, whatever kills it (its own weapon, a drowning, a fall,
 * leaving the map), or when it sets off an ice mine (frozen or not, it moves no more).
 */
function turnCutShort(state: GameState): boolean {
  const m = state.match!;
  if (!m.locked || (m.phase !== PHASE_AIM && m.phase !== PHASE_RETREAT)) return false;
  for (const e of state.events) {
    if (e.type === 'iceMine' && e.unit === m.selected) return true;
    if (e.type === 'death' && e.unit === m.selected) return true;
  }
  return false;
}

/** Water touching Lavaman sets into obsidian around him. */
function coatInObsidian(state: GameState, u: Unit): void {
  const { w, h, data } = state.world.physique;
  const cx = u.x >> FP_SHIFT;
  const cy = u.y >> FP_SHIFT;
  for (let y = Math.max(0, cy - 1); y <= Math.min(h - 1, cy + UNIT_H); y++) {
    for (let x = Math.max(0, cx - 1); x <= Math.min(w - 1, cx + UNIT_W); x++) {
      if (data[y * w + x] === WATER) { data[y * w + x] = OBSIDIAN; state.world.cellState.data[y * w + x] = 0; }
    }
  }
}

/** Victory as soon as one player alone has living units; a draw if the last ones die on the same tick. */
function checkVictory(state: GameState): boolean {
  const m = state.match!;
  let alive = 0;
  let last = -1;
  for (let p = 0; p < m.config.players; p++) if (hasLivingUnits(state, p)) { alive++; last = p; }
  if (alive > 1) return false;
  m.phase = PHASE_OVER;
  m.phaseTick = 0;
  m.winner = alive === 1 ? last : DRAW;
  stopWalking(state);
  return true;
}

/**
 * Filters a command under match rules and applies the rule-only ones. Returns true if tick() must
 * still apply its physical effect (walk, jump, fire).
 */
export function applyMatchCommand(state: GameState, cmd: Command): boolean {
  const m = state.match!;
  if (isSandboxCommand(cmd) || m.phase === PHASE_OVER) return false;
  if (cmd.type === 'forfeit') { forfeit(state, cmd.player); return false; }
  if (cmd.player !== m.currentPlayer) return false;
  // A frozen unit does nothing (author's rule): no step, no jump, no flight, no drill, no shot.
  if ('unit' in cmd && cmd.type !== 'select' && cmd.type !== 'aim' && cmd.type !== 'release' && !(cmd.type === 'walk' && cmd.dir === 0)) {
    const actor = state.units[cmd.unit];
    if (actor && actor.frozen !== 0) return false;
  }
  switch (cmd.type) {
    case 'place':
      if (m.phase === PHASE_PLACEMENT) place(state, cmd.player, cmd.kind, Math.trunc(cmd.x), Math.trunc(cmd.y));
      return false;
    case 'select': {
      if (m.phase !== PHASE_AIM || m.locked) return false;
      const u = state.units[cmd.unit];
      if (u && playable(u) && u.player === cmd.player) m.selected = u.id;
      return false;
    }
    case 'lock':
      if (m.phase === PHASE_AIM && !m.locked && m.selected >= 0 && playable(state.units[m.selected])) {
        m.locked = true;
        const units = playerUnits(state, cmd.player);
        m.rotation[cmd.player] = (units.findIndex((u) => u.id === m.selected) + 1) % units.length;
      }
      return false;
    case 'unlock':
      if (m.phase === PHASE_AIM && m.locked && !m.moved && !m.fired && m.pistolShots === 0) m.locked = false;
      return false;
    case 'aim':
    case 'release':
      return (m.phase === PHASE_AIM || m.phase === PHASE_RETREAT) && m.locked && cmd.unit === m.selected;
    case 'walk':
    case 'jump':
    case 'thrust':
      if ((m.phase !== PHASE_AIM && m.phase !== PHASE_RETREAT) || !m.locked || cmd.unit !== m.selected) return false;
      if (cmd.type === 'jump' || (cmd.type === 'walk' && cmd.dir !== 0)) m.moved = true;
      return true;
    case 'jetpack': {
      // A move, not the shot: during the aim or the retreat, while there is one left.
      if ((m.phase !== PHASE_AIM && m.phase !== PHASE_RETREAT) || !m.locked || cmd.unit !== m.selected) return false;
      const slot = cmd.player * WEAPONS.length + WEAPON_JETPACK;
      if (m.ammo[slot] === 0 || state.units[cmd.unit].jet > 0) return false;
      if (m.ammo[slot] > 0) m.ammo[slot]--;
      m.moved = true;
      return true;
    }
    case 'drill': {
      // A move, not the shot (author's change): during the aim or the retreat, while there is one left.
      if ((m.phase !== PHASE_AIM && m.phase !== PHASE_RETREAT) || !m.locked || cmd.unit !== m.selected) return false;
      const slot = cmd.player * WEAPONS.length + WEAPON_DRILL;
      if (m.ammo[slot] === 0 || state.units[cmd.unit].drill > 0) return false;
      if (m.ammo[slot] > 0) m.ammo[slot]--;
      m.moved = true;
      return true;
    }
    case 'endTurn':
      // The player ends the turn early: the resolution, then the next player.
      if (m.phase === PHASE_AIM || m.phase === PHASE_RETREAT || m.phase === PHASE_GHOST) toResolve(state);
      return false;
    case 'ghost':
      if (m.phase === PHASE_GHOST && ghostAction(state, cmd.player, cmd.action, Math.trunc(cmd.x), Math.trunc(cmd.y))) toResolve(state);
      return false;
    case 'fire': {
      if (m.phase !== PHASE_AIM || !m.locked || m.fired || cmd.unit !== m.selected || !isWeapon(cmd.weapon)) return false;
      if (cmd.weapon === WEAPON_JETPACK || cmd.weapon === WEAPON_DRILL) return false; // moves, with their own commands
      // The pistol (author's rule): PISTOL_SHOTS bullets in the turn, one ammunition for all; nothing else after the first.
      if (m.pistolShots > 0 && cmd.weapon !== WEAPON_PISTOL) return false;
      const slot = cmd.player * WEAPONS.length + cmd.weapon;
      if (m.pistolShots === 0) {
        if (m.ammo[slot] === 0) return false;
        if (m.ammo[slot] > 0) m.ammo[slot]--;
      }
      if (cmd.weapon === WEAPON_PISTOL && ++m.pistolShots < PISTOL_SHOTS) return true;
      m.fired = true;
      m.phase = PHASE_RETREAT;
      m.phaseTick = 0;
      return true;
    }
  }
  return false;
}

/** Advances the timers and phases after the physics of a tick. */
export function matchAfterTick(state: GameState): void {
  const m = state.match!;
  if (m.phase === PHASE_OVER) return;
  m.phaseTick++;
  if (m.phase === PHASE_PLACEMENT) {
    // No free placement: the computer puts every invader at once.
    if (m.config.autoPlacement) {
      for (let i = 0; i < MAX_UNITS_ON_MAP && m.phase === PHASE_PLACEMENT; i++) if (!autoPlace(state)) break;
    } else if (m.phaseTick >= PLACEMENT_TICKS) autoPlace(state);
    return;
  }
  lavaBurn(state);
  if (checkVictory(state)) return;
  if (turnCutShort(state)) toResolve(state);
  if (m.phase === PHASE_AIM && m.phaseTick >= aimTicks(m)) toResolve(state);
  else if (m.phase === PHASE_GHOST && m.phaseTick >= GHOST_TICKS) toResolve(state);
  else if (m.phase === PHASE_RETREAT && m.phaseTick >= RETREAT_TICKS) toResolve(state);
  else if (m.phase === PHASE_RESOLVE) {
    const calm = state.projectiles.length === 0 && state.surges.length === 0 && state.jets.length === 0 && state.geysers.every((g) => g.wait > 0) && !state.barrels.some(barrelMoving) && state.units.every((u) => isSettled(state.world, u));
    if ((calm && m.phaseTick >= RESOLVE_MIN_TICKS) || m.phaseTick >= RESOLVE_MAX_TICKS) {
      endOfTurnDamage(state);
      if (!checkVictory(state)) startTurn(state, nextPlayer(state));
    }
  }
}

/** Ticks left in the current phase's timer (placement, aim, retreat), or 0. */
export function phaseTicksLeft(m: MatchState): number {
  const total = m.phase === PHASE_PLACEMENT ? PLACEMENT_TICKS : m.phase === PHASE_AIM ? aimTicks(m) : m.phase === PHASE_RETREAT ? RETREAT_TICKS
    : m.phase === PHASE_GHOST ? GHOST_TICKS : 0;
  return total > m.phaseTick ? total - m.phaseTick : 0;
}
